import AntdTable          from 'antd/lib/table';
import AntdEmpty          from 'antd/lib/empty';
import { ColumnType }     from 'antd/lib/table';
import Button             from '@widesk-ui/components/Button';
import { InitialFields }  from '@widesk-ui/components/FilterBar';
import Form               from '@widesk-ui/components/form/Form';
import React              from 'react';
import TableStore         from '@widesk-ui/stores/TableStore';
import Values             from '@widesk-ui/components/Values';
import { Modal }          from '@widesk-ui/components/modal/Modal';
import { ReloadOutlined } from '@ant-design/icons';
import { observer }       from 'mobx-react-lite';
import { useBox }         from '@widesk-ui/hooks/useBox';
import Typography         from 'antd/lib/typography';
import useDidMount        from '@widesk-core/hooks/useDidMount';
import Value              from '@widesk-ui/components/Value';
import { Action }         from '@widesk-ui/components/ButtonActions';
import ButtonActions      from '@widesk-ui/components/ButtonActions';
import useDashboard       from '@widesk-ui/hooks/useDashboard';
import useTheme           from '@widesk-ui/hooks/useTheme';
import Observer           from '@widesk-ui/components/Observer';
import _flatten           from 'lodash/flatten';
import { Column }         from '@widesk-ui/components/table/Table';
import { TableProps }     from '@widesk-ui/components/table/Table';
import { ClearOutlined }  from '@ant-design/icons';

export type TableMode = 'default' | 'vertical' | 'vertical-col';

const Table = observer(<D extends any[], F extends InitialFields>(props: TableProps<D, F> & {
	tableStore: TableStore;
	components?: Pick<React.ComponentProps<typeof AntdTable>, 'components'>
}) => {
	const tableStore = props.tableStore;
	const theme = useTheme();

	const { boxStore } = useBox();
	const { dashboardStore } = useDashboard();
	const { renderHeader, renderFooter } = props;

	useDidMount(() => props.onDidMount ? props.onDidMount(tableStore) : undefined);

	tableStore.setProps(props);

	useDidMount(() => dashboardStore?.pushTableStore(tableStore));

	const [directions, setDirections] = React.useState(props.columns.map(column => ({
		sortOrder: column.sortOrder,
		columnKey: column.sortBy,
	})));

	if (props.filters) {
		Object.entries(tableStore.filters).map(([key, value]) => {
			const initialFilter = props.filters?.[key];
			if (initialFilter) {
				initialFilter.value = value;
			}
		});
	}

	const submitFilters = async (values: Record<string, unknown>) => {
		updateBoxTopRight(values);
		return tableStore.fetch(1, tableStore.pageSize, values);
	};

	const updateBoxTopRight = (filters: Record<string, unknown>) => {
		const prevFilterKeys = Object.keys(tableStore.prevFilters)
			.filter(key => !key.startsWith('order['));
		const prevFilterCount = prevFilterKeys
			.filter(key => !!tableStore.prevFilters[key])
			.length;

		boxStore?.setCtxTopRight(
			<>
				{!!Object.keys(props.filters || {}).length && (
					<Button
						onClick={() => {
							Modal.open({
								title: 'Filtres',
								children: (
									<Form
										onDidMount={async formTools => formTools.setFieldsValue(filters as any)}
										fields={props.filters}
										onSubmit={submitFilters}
									/>
								),
							});
						}}
					>
						Filtres{prevFilterCount ? `(${prevFilterCount})` : ''}
					</Button>
				)}

				{prevFilterCount ? (
					<Button onClick={() => tableStore.fetch(tableStore.page, tableStore.pageSize, {})}>
						<ClearOutlined />
					</Button>
				) : undefined}

				<Button disabled={tableStore.isLoading} onClick={() => tableStore.reload()}>
					<ReloadOutlined spin={tableStore.isLoading} />
				</Button>
			</>,
		);
	};

	useDidMount(async () => {
		await props.onFetchStart?.(tableStore);
		await tableStore.fetch(1, tableStore.pageSize, tableStore.filters);
		await props.onFetchEnd?.(tableStore);
	});

	React.useEffect(() => updateBoxTopRight(tableStore.filters), [tableStore.isLoading]);

	const mode = props.mode;

	const columns = React.useMemo(() => {
		const actions = props.actions ? (r: any) => props.actions!(r, tableStore) : undefined;
		return refactorColumnsForAntd(props.columns, mode, directions, actions);
	}, [props.columns, mode, directions]);

	const locale = typeof props.locale?.emptyText === 'string'
		? {
			...props.locale,
			emptyText: <AntdEmpty image={AntdEmpty.PRESENTED_IMAGE_SIMPLE} description={props.locale.emptyText} />,
		}
		: props.locale;

	return (
		<AntdTable
			key={tableStore.data.map(d => props.rowKey(d)).join('.')}
			style={{ flex: 1 }}
			loading={tableStore.isLoading}
			columns={columns}
			onChange={(pag: any, _, sorter: any) => {
				setDirections(directions.map(direction => {
					if (direction.columnKey === sorter.columnKey) {
						return {
							...direction,
							sortOrder: sorter.order,
						};
					}
					return direction;
				}));

				return tableStore.fetch(pag.current, pag.pageSize, {
					...tableStore.filters,
					[sorter.columnKey]: sorter.order ? (sorter.order === 'ascend' ? 'asc' : sorter.order === 'descend' ? 'desc' : undefined) : undefined,
				});
			}}
			dataSource={tableStore.data}
			rowKey={props.rowKey}
			expandable={props.rowExpandable ? {
				...props.rowExpandable,
				expandedRowRender: props.rowExpandable?.expandedRowRender,
				onExpand: props.rowExpandable?.onExpand,
			} : undefined}
			rowSelection={props.rowSelection ? {
				...props.rowSelection,
				getCheckboxProps: props.rowSelection?.getCheckboxProps,
			} : undefined}
			title={renderHeader ? () => renderHeader(tableStore) : undefined}
			footer={renderFooter ? () => renderFooter(tableStore) : undefined}
			bordered={props.bordered}
			locale={locale}
			scroll={props.scroll}
			size={props.size || 'small'}
			pagination={props.pagination === false ? false : {
				defaultPageSize: 10,
				simple: true,
				hideOnSinglePage: true,
				size: 'small',
				showSizeChanger: true,
				showLessItems: true,
				className: 'widesk-table-ant-pagination',
				style: {
					paddingLeft: theme.marginLG,
					paddingRight: theme.marginLG,
				},
				...props.pagination,

				...(
					props.pagination?.pagedCollection ? {
						total: props.pagination.pagedCollection.total,
						current: props.pagination.pagedCollection.page,
						pageSize: props.pagination.pagedCollection.itemsPerPage,
					} : {
						total: tableStore.data.length,
						pageSize: props.pagination?.pageSize || 10,
					}
				),
			}}
			components={props.components}
		/>
	);
});

const refactorColumnsForAntd = (
	columns: Column<any>[],
	mode: TableMode = 'default',
	directions: Array<{ sortOrder?: 'ascend' | 'descend', columnKey?: string }>,
	actions?: (d: any) => Action[],
) => {
	const cols: ColumnType<any>[] = [];

	const visibleColumns = columns.filter(c => c.hidden !== true);

	if (mode === 'vertical' || mode === 'vertical-col') {
		const isSortable = visibleColumns.at(0)?.reference === 'sortable-column';
		const verticalColumns = _flatten(
			visibleColumns
				.filter(c => c.reference !== 'sortable-column')
				.map(c => c.children || c),
		);

		if (isSortable) {
			cols.push(visibleColumns.at(0) as any);
		}

		cols.push({
			render: (_text, record, index) => {
				return (
					<Observer render={() => {
						return (
							<Values columns={[verticalColumns.map(c => ({
								label: c.title,
								style: c.style ? c.style(record, index) : undefined,
								value: c.render?.(record, index),
								type: c.type,
								vertical: mode === 'vertical-col',
							}))]} />
						);
					}} />
				);
			},
		});
	} else {
		visibleColumns.map(column => {
			cols.push({
				key: column.sortBy,
				sorter: !!column.sortBy,
				...column,
				sortOrder: directions.find(direction => direction.columnKey === column.sortBy)?.sortOrder,
				title: typeof column.title === 'string' ? (
					<Typography.Text ellipsis style={{ color: 'inherit', maxWidth: '100%' }} title={column.title}>
						{column.title}
					</Typography.Text>
				) : column.title,
				render: column.render ? (_text, record, index) => {
					return (
						<Value
							value={() => column.render(record, index)}
							style={column.style ? column.style(record, index) : undefined}
							type={column.type}
						/>
					);
				} : undefined,
				// eslint-disable-next-line @typescript-eslint/ban-ts-comment
				// @ts-ignore
				children: refactorColumnsForAntd(column.children || [], mode, directions),
			});
		});
	}

	if (actions) {
		cols.push({
			fixed: 'right',
			align: 'right',
			render: (_text, record) => <ButtonActions size="small" actions={actions ? actions(record) : []} />,
		});
	}

	return cols;
};

export default Table;
